// src/pages/OfficialDashboard.js
// Game-official console: My Games (league-assigned, upcoming) + History
// (completed). A league admin assigns an official directly to a game now —
// there is no more apply/waitlist/available-games flow (removed 2026-07-30;
// see ios/Lambda/polyp-official-games/index.mjs's header for the full
// removal rationale — the old apply/cancel UI is preserved there behind
// #if OFFICIAL_APPLY_FLOW on iOS, but this web file had no such flag so the
// equivalent code was deleted outright). Every game reaching either list is
// already assigned to the caller (server-scoped by
// LeagueGames.officialId-startsAt-index), so stat entry is unconditional.
// Gated by RoleGate require="official"; the approval gate itself is handled
// here — pending/denied officials see a wait/denied notice (no game fetches)
// until an admin approves them from /admin/officials. Mirrors
// AdminMatchStats' list → detail state machine, wired to /official endpoints.
// The stat-entry form itself moved to components/shared/GameStatsEditor
// (2026-09-15) when the league console needed the same one — this page keeps
// the list, the approval gate, and the choice of which game is open.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import ApiService from "../services/api.service";
import AuthService from "../services/auth.service";
import { useRole } from "../components/shared/RoleContext";
import GameStatsEditor from "../components/shared/GameStatsEditor";
import ConsoleSwitcher from "../components/shared/ConsoleSwitcher";
import { localWhen, localWhenLine } from "../utils/gameTime";

// This envelope calls the venue wall-clock date `agreedDate`, not `gameDate`
// (polyp-official-games kept the old matchmaking field name), and an official's
// assignment list shows the year — matching the iOS official list's
// `.weekdayMonthDayYear` style. See utils/gameTime.js.
const OFFICIAL_WHEN = { includeYear: true, venueDate: "agreedDate" };

const C = {
  bg: "#1a1a19",
  surface: "#222220",
  surface2: "#2c2c29",
  border: "#333330",
  text: "#eaeae8",
  textDim: "#9a9994",
  accent: "#33cc66",
  blue: "#0A84FF",
};


export default function OfficialDashboard() {
  const navigate = useNavigate();
  const { role, refresh } = useRole();
  const officialStatus = role?.officialStatus || null;
  // Unified PW Manager (2026-09-24): no more pending-approval wall. A scorer
  // can only score games a league or coach assigned them, and assignment
  // needs their invite, so approval gated nothing real. Every staff account
  // reaches this console; only an admin's explicit "denied" is still shown.
  const approved = officialStatus !== "denied";

  const [scope, setScope] = useState("upcoming"); // 'upcoming' | 'history'
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [checking, setChecking] = useState(false); // pending notice "Check again"

  // The editor itself (fetch, form state, save, the 409 handshake) lives in
  // the shared GameStatsEditor — extracted 2026-09-15 so the league console
  // could mount the same form. This page only decides WHICH game is open.
  const [selected, setSelected] = useState(null); // game summary from "upcoming"/"history"

  const loadGames = useCallback(async (which) => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.getOfficialGames(which);
      setGames(res?.games || []);
    } catch (err) {
      setError(err.message || "Failed to load games.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!approved) return; // no game fetches until the account is approved
    loadGames(scope);
  }, [approved, scope, loadGames]);

  const switchScope = (which) => {
    if (which === scope) return;
    setSelected(null);
    setScope(which);
  };

  const openStats = (game) => {
    setError(null);
    setSelected(game);
  };

  const backToList = () => setSelected(null);

  const onSaved = async () => {
    setToast("Stats saved.");
    await loadGames(scope); // keep the list's score fresh
    setTimeout(() => setToast(null), 3500);
  };

  const handleSignOut = async () => {
    try { await AuthService.logout(); } catch (_) {}
    navigate("/login", { replace: true });
  };

  // ── Pending / denied notice — no game access until approved ─────────────
  if (!approved) {
    const denied = officialStatus === "denied";
    const checkAgain = async () => {
      setChecking(true);
      try { await refresh(); } finally { setChecking(false); }
    };
    return (
      <div style={{
        minHeight: "100vh", background: C.bg, color: C.text, display: "grid",
        placeItems: "center", padding: 24, fontFamily: "var(--font-primary, sans-serif)",
      }}>
        <div style={{
          width: "100%", maxWidth: 460, background: C.surface,
          border: `1px solid ${C.border}`, borderRadius: 18,
          padding: "32px 28px", textAlign: "center",
        }}>
          <div style={{ fontSize: 44, marginBottom: 12 }} aria-hidden>🏀</div>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 10px" }}>
            {denied ? "Game Official access denied" : "Awaiting approval"}
          </h1>
          <p style={{ color: C.textDim, fontSize: 15, lineHeight: 1.5, margin: 0 }}>
            {denied
              ? "Your Game Official account request was denied. If you think this is a mistake, contact help@polyp.me."
              : "Your Game Official account is awaiting approval. We review new officials manually — check back soon."}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 22 }}>
            <button onClick={checkAgain} disabled={checking} style={btn(checking ? C.surface2 : C.accent, checking ? C.textDim : "#0b1f12")}>
              {checking ? "Checking…" : "Check again"}
            </button>
            <button onClick={handleSignOut} style={btn(C.surface2)}>Sign out</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Approved: game lists + stat entry ────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "var(--font-primary, sans-serif)" }}>
      {/* Top bar with scope tabs */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px", borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            Polyp <span style={{ color: C.textDim, fontWeight: 400 }}>Games</span>
          </div>
          <nav style={{ display: "flex", gap: 6 }}>
            <button onClick={() => switchScope("upcoming")} style={tab(scope === "upcoming")}>My Games</button>
            <button onClick={() => switchScope("history")} style={tab(scope === "history")}>History</button>
          </nav>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <ConsoleSwitcher variant="bar" />
          <button onClick={() => loadGames(scope)} style={btn(C.surface2)}>Refresh</button>
          <button onClick={handleSignOut} style={btn(C.surface2)}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: 24 }}>
        {toast && (
          <div style={{ background: `${C.accent}22`, color: C.accent, padding: "10px 14px", borderRadius: 10, marginBottom: 16 }}>
            {toast}
          </div>
        )}
        {error && (
          <div style={{ background: "#ff6b6b22", color: "#ff8f8f", padding: "10px 14px", borderRadius: 10, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {!selected ? (
          <>
            <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 1.2, color: C.textDim, marginBottom: 12 }}>
              {scope === "upcoming" ? "My games" : "History"} {loading ? "" : `(${games.length})`}
            </div>
            {loading ? (
              <p style={{ color: C.textDim }}>Loading…</p>
            ) : games.length === 0 ? (
              <p style={{ color: C.textDim }}>
                {scope === "upcoming"
                  ? "No upcoming games yet. Your league admin assigns you directly."
                  : "No completed games yet. Games you track will show up here."}
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {games.map((g) => {
                  const completed = g.status === "completed";
                  return (
                    <div key={g.matchId} style={{
                      background: C.surface, border: `1px solid ${C.border}`,
                      borderRadius: 12, padding: 16,
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
                    }}>
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                          {g.businessNameA} <span style={{ color: C.textDim }}>vs</span> {g.businessNameB}
                        </div>
                        <div style={{ color: C.textDim, fontSize: 14 }}>
                          {localWhenLine(g, OFFICIAL_WHEN)}
                          {g.sport ? ` · ${g.sport[0].toUpperCase() + g.sport.slice(1)}` : ""}
                          {completed && typeof g.scoreA === "number" ? ` · ${g.scoreA}–${g.scoreB}` : ""}
                        </div>
                        {g.leagueName && (
                          <div style={{ marginTop: 6 }}>
                            <span style={{
                              fontSize: 12, fontWeight: 600, color: C.textDim, background: C.surface2,
                              padding: "3px 9px", borderRadius: 999,
                            }}>
                              {g.leagueName}
                            </span>
                          </div>
                        )}
                        {/* A tournament game is just a LeagueGames row with these
                            extra fields — mirrors LeagueSchedulePage's badge. */}
                        {g.tournamentRoundName && (
                          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 4, color: C.textDim, fontSize: 12 }}>
                            <Trophy size={12} /> {g.tournamentName || "Tournament"} · {g.tournamentRoundName}
                          </div>
                        )}
                      </div>
                      {/* Every game reaching this list is already assigned to the
                          caller (server-scoped by LeagueGames.officialId) — stat
                          entry is unconditional, not gated on a per-role check. */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <button onClick={() => openStats(g)} style={btn(C.blue, "#fff")}>
                          {scope === "upcoming" ? "Enter stats" : "Edit stats"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
              <button onClick={backToList} style={btn(C.surface2)}>← My Games</button>
              <div style={{ fontSize: 16, fontWeight: 700, flex: 1, minWidth: 200 }}>
                {selected.businessNameA} vs {selected.businessNameB}
                <span style={{ color: C.textDim, fontWeight: 400 }}> · {localWhen(selected, OFFICIAL_WHEN).date}</span>
              </div>
            </div>

            <GameStatsEditor
              matchId={selected.matchId}
              teamAName={selected.businessNameA}
              teamBName={selected.businessNameB}
              onSaved={onSaved}
              onCancel={backToList}
            />
          </>
        )}
      </div>
    </div>
  );
}

function btn(bg, color = "#eaeae8") {
  return {
    background: bg, color, border: "none", borderRadius: 8,
    padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer",
  };
}
function tab(active) {
  return {
    background: active ? "#33cc6622" : "transparent",
    color: active ? "#33cc66" : "#9a9994",
    border: "none", borderRadius: 8, padding: "6px 12px",
    fontSize: 14, fontWeight: 600, cursor: "pointer",
  };
}
